import type { Baustein } from "../engine/types"

/**
 * Demo Bausteine catalog — 61 entries across Regulatorik, Fach, Führung,
 * Digital, Methodik, and the on-the-job / social entries that give the
 * 70-20-10 framework a mix to actually steer.
 *
 * Fully synthetic. Every provider, host and course id is a placeholder —
 * `learn.acme-bank.ch`, "Acme Bank" — deliberately, so nothing in a public
 * demo is mistaken for a real institution's real training catalogue.
 *
 * The field set is the point of this file: skills + level + duration +
 * prerequisites + format is the minimum that makes composition possible at
 * all. A real catalog that lacks `durationHours` cannot be budgeted, and one
 * that lacks `teaches` cannot be matched. See the plan's §3.3.
 */
export const BAUSTEINE: Baustein[] = [
    // ========================================================================
    // Regulatorik
    // ========================================================================
    {
        id: "fidleg-basis",
        title: "FIDLEG: Grundlagen und Verhaltensregeln",
        description:
            "Pflichten nach Finanzdienstleistungsgesetz: Informations-, Dokumentations- und Rechenschaftspflichten, Kundensegmentierung, Interessenkonflikte.",
        teaches: [
            { skillId: "reg.fidleg", level: 2 },
            { skillId: "reg.marktverhalten", level: 1 },
        ],
        durationHours: 4,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "fidleg-refresher",
        evidence: "entscheidungstest",
        title: "FIDLEG-Refresher für Kundenberatende",
        description:
            "Jährliche Auffrischung: Praxisfälle aus der Beratung, Änderungen in Weisungen und Aufsichtspraxis, häufige Feststellungen der internen Revision.",
        teaches: [{ skillId: "reg.fidleg", level: 3 }],
        durationHours: 2,
        format: "e-learning",
        prerequisites: ["fidleg-basis"],
        level: 2,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "suitability",
        title: "Angemessenheits- und Eignungsprüfung in der Praxis",
        description:
            "Wann welche Prüfung greift, wie das Risikoprofil erhoben und dokumentiert wird, Umgang mit Kundenwünschen ausserhalb des Profils.",
        teaches: [
            { skillId: "reg.suitability", level: 3 },
            { skillId: "reg.fidleg", level: 2 },
        ],
        durationHours: 4,
        format: "webinar",
        prerequisites: ["fidleg-basis"],
        level: 2,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "gwg-basis",
        title: "Geldwäschereiprävention: Grundlagen",
        description:
            "GwG-Pflichten, Risikoindikatoren, Verdachtsmeldung an die MROS, Rolle der Geldwäschereifachstelle.",
        teaches: [
            { skillId: "reg.gwg", level: 2 },
            { skillId: "reg.kyc", level: 1 },
        ],
        durationHours: 3,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "gwg-refresher",
        evidence: "entscheidungstest",
        title: "GwG-Refresher mit Fallbeispielen",
        description:
            "Jährliche Auffrischung anhand anonymisierter Fälle aus der Bank, aktuelle Typologien und Praxis der MROS.",
        teaches: [{ skillId: "reg.gwg", level: 3 }],
        durationHours: 2,
        format: "e-learning",
        prerequisites: ["gwg-basis"],
        level: 2,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "vsb-kyc",
        evidence: "entscheidungstest",
        title: "VSB und KYC: Sorgfaltspflichten korrekt umsetzen",
        description:
            "Identifikation der Vertragspartei, Feststellung der wirtschaftlich berechtigten Person, Dokumentationsanforderungen, Nachträge und Nachbesserungen.",
        teaches: [
            { skillId: "reg.vsb", level: 3 },
            { skillId: "reg.kyc", level: 3 },
        ],
        durationHours: 4,
        format: "praesenz",
        prerequisites: ["gwg-basis"],
        level: 1,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "datenschutz",
        evidence: "wissenstest",
        title: "Datenschutz (revDSG) im Bankalltag",
        description:
            "Bearbeitungsgrundsätze, Auskunftsrecht, Meldung von Verletzungen der Datensicherheit, Umgang mit Kundendaten in Drittsystemen.",
        teaches: [
            { skillId: "reg.datenschutz", level: 2 },
            { skillId: "reg.bankkundengeheimnis", level: 2 },
        ],
        durationHours: 2,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Legal & Compliance",
    },
    {
        id: "infosec-basis",
        evidence: "wissenstest",
        title: "Informationssicherheit für alle Mitarbeitenden",
        description:
            "Phishing, Umgang mit Passwörtern und MFA, Clean Desk, Meldung von Sicherheitsvorfällen, Klassifikation von Informationen.",
        teaches: [{ skillId: "reg.infosec", level: 2 }],
        durationHours: 1.5,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Information Security",
    },
    {
        id: "sanktionen",
        evidence: "wissenstest",
        title: "Sanktionen und Embargos",
        description:
            "SECO-Listen, Screening-Prozesse, Umgang mit Treffern, Eskalationswege bei Sanktionsbezug.",
        teaches: [{ skillId: "reg.sanktionen", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        prerequisites: ["gwg-basis"],
        level: 1,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "op-risk",
        title: "Operationelles Risiko und internes Kontrollsystem",
        description:
            "Risikoinventar, Schlüsselkontrollen, Verlustdatensammlung, Zusammenspiel mit der internen Revision.",
        teaches: [{ skillId: "reg.opRisk", level: 2 }],
        durationHours: 3,
        format: "webinar",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Risk Management",
    },
    {
        id: "bcm",
        title: "Business Continuity Management: Rollen und Ernstfall",
        description:
            "BCM-Organisation, kritische Prozesse, Wiederanlaufpläne, Übungen und Meldewege bei Ausfällen.",
        teaches: [
            { skillId: "reg.bcm", level: 2 },
            { skillId: "reg.opRisk", level: 2 },
        ],
        durationHours: 3,
        format: "praesenz",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "IT Risk & BCM",
    },
    {
        id: "outsourcing",
        title: "Auslagerungen steuern (FINMA-RS 18/3)",
        description:
            "Wesentlichkeitsbeurteilung, Auslagerungsinventar, Anforderungen an Dienstleister, laufende Überwachung.",
        teaches: [
            { skillId: "reg.outsourcing", level: 3 },
            { skillId: "reg.opRisk", level: 2 },
        ],
        durationHours: 4,
        format: "webinar",
        prerequisites: ["op-risk"],
        level: 2,
        language: "de",
        provider: "Risk Management",
    },

    {
        id: "kyc-basis",
        title: "KYC: Grundlagen und Praxis",
        description:
            "Identifikation und Verifikation, wirtschaftlich Berechtigte, PEP-Abklärungen und laufende Überwachung der Geschäftsbeziehung.",
        teaches: [{ skillId: "reg.kyc", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "vsb-sorgfaltspflichten",
        title: "VSB: Sorgfaltspflichten in der Praxis",
        description:
            "Formularwesen, Dokumentationspflichten und typische Feststellungen aus der VSB-Prüfung, mit Fallbeispielen aus dem Tagesgeschäft.",
        teaches: [{ skillId: "reg.vsb", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "marktverhalten-basis",
        title: "Marktverhaltensregeln",
        description:
            "Insiderinformationen, Marktmanipulation, Eigengeschäfte von Mitarbeitenden und Meldepflichten gegenüber der Aufsicht.",
        teaches: [{ skillId: "reg.marktverhalten", level: 2 }],
        durationHours: 1.5,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "bankkundengeheimnis",
        title: "Bankkundengeheimnis",
        description:
            "Reichweite und Grenzen des Bankkundengeheimnisses, Amts- und Rechtshilfe, Auskünfte an Dritte und Verhalten bei Anfragen.",
        teaches: [{ skillId: "reg.bankkundengeheimnis", level: 2 }],
        durationHours: 1,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Legal & Compliance",
    },
    {
        id: "suitability-elearning",
        title: "Angemessenheit und Eignung: E-Learning",
        description:
            "Wann welche Prüfung greift, Erhebung des Risikoprofils und Dokumentation — als kompakte Auffrischung zwischen den Präsenzterminen.",
        teaches: [{ skillId: "reg.suitability", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: ["fidleg-basis"],
        level: 1,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "bcm-basis",
        title: "Business Continuity: Grundlagen",
        description:
            "Kritische Prozesse, Wiederanlaufzeiten, Notfallorganisation und die eigene Rolle im Ereignisfall.",
        teaches: [{ skillId: "reg.bcm", level: 2 }],
        durationHours: 1.5,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "IT Risk & BCM",
    },
    // ========================================================================
    // Fach — Beratung & Anlage
    // ========================================================================
    {
        id: "beratungsprozess",
        title: "Der Acme Bank Beratungsprozess",
        description:
            "Von der Bedarfsanalyse bis zur Nachbetreuung: Struktur, Werkzeuge und Dokumentation des Beratungsgesprächs.",
        teaches: [
            { skillId: "fach.beratungsprozess", level: 2 },
            { skillId: "fach.produktkenntnis", level: 1 },
        ],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "gespraechsfuehrung",
        title: "Gesprächsführung und Einwandbehandlung",
        description:
            "Fragetechnik, aktives Zuhören, Umgang mit Einwänden und Preisdiskussionen, Abschlussorientierung.",
        teaches: [
            { skillId: "fach.gespraechsfuehrung", level: 3 },
            { skillId: "fach.einwandbehandlung", level: 3 },
        ],
        durationHours: 8,
        format: "praesenz",
        prerequisites: ["beratungsprozess"],
        level: 1,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "anlageberatung-basis",
        title: "Anlageberatung: Grundlagen",
        description:
            "Anlageklassen, Risiko und Rendite, Anlagestrategien der Acme Bank, Rolle des Anlagekomitees.",
        teaches: [
            { skillId: "fach.anlageberatung", level: 2 },
            { skillId: "fach.wertschriften", level: 2 },
        ],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "portfoliokonstruktion",
        title: "Portfoliokonstruktion und Rebalancing",
        description:
            "Strategische und taktische Allokation, Diversifikation, Kosten, systematisches Rebalancing im Kundendepot.",
        teaches: [
            { skillId: "fach.portfolio", level: 3 },
            { skillId: "fach.anlageberatung", level: 3 },
        ],
        durationHours: 6,
        format: "webinar",
        prerequisites: ["anlageberatung-basis"],
        level: 2,
        language: "de",
        provider: "Investment Center",
    },
    {
        id: "esg-grundlagen",
        title: "Nachhaltigkeit und ESG: Grundlagen",
        description:
            "ESG-Kriterien, Anlageansätze, Greenwashing-Risiken, regulatorischer Rahmen in der Schweiz.",
        teaches: [{ skillId: "fach.esg", level: 2 }],
        durationHours: 3,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Investment Center",
    },
    {
        id: "esg-beratung",
        title: "ESG im Kundengespräch",
        description:
            "Nachhaltigkeitspräferenzen erheben und dokumentieren, ESG-Produkte erklären, kritische Kundenfragen beantworten.",
        teaches: [
            { skillId: "fach.esgBeratung", level: 3 },
            { skillId: "fach.esg", level: 3 },
        ],
        durationHours: 4,
        format: "webinar",
        prerequisites: ["esg-grundlagen"],
        level: 2,
        language: "de",
        provider: "Investment Center",
    },
    {
        id: "vorsorge-basis",
        title: "Vorsorge Schweiz: Säule 2 und 3",
        description:
            "Drei-Säulen-System, Einkauf in die Pensionskasse, Säule 3a/3b, Vorbezug für Wohneigentum.",
        teaches: [
            { skillId: "fach.vorsorge", level: 2 },
            { skillId: "fach.steuern", level: 1 },
        ],
        durationHours: 6,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "finanzplanung",
        title: "Ganzheitliche Finanzplanung",
        description:
            "Lebensphasenorientierte Planung, Pensionierungsplanung, Nachlass und Güterrecht, Zusammenspiel mit Steuern.",
        teaches: [
            { skillId: "fach.finanzplanung", level: 3 },
            { skillId: "fach.vorsorge", level: 3 },
            { skillId: "fach.steuern", level: 2 },
        ],
        durationHours: 16,
        format: "praesenz",
        prerequisites: ["vorsorge-basis"],
        level: 2,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "steuern-privatkunden",
        title: "Steuern für Privatkunden",
        description:
            "Einkommens- und Vermögenssteuer, Abzüge, steuerliche Wirkung von Vorsorge- und Anlageentscheiden.",
        teaches: [{ skillId: "fach.steuern", level: 2 }],
        durationHours: 4,
        format: "webinar",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Vertriebsausbildung",
    },

    {
        id: "produktkenntnis-refresher",
        title: "Produktkenntnis: Jahres-Refresher",
        description:
            "Neuerungen im Produktsortiment, angepasste Konditionen und ausser Kraft gesetzte Produkte — kompakt und mit Wissenstest.",
        teaches: [{ skillId: "fach.produktkenntnis", level: 2 }],
        durationHours: 1.5,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    // ========================================================================
    // Fach — Kredit & Finanzierung
    // ========================================================================
    {
        id: "hypotheken-basis",
        title: "Hypothekargeschäft: Grundlagen",
        description:
            "Produkte und Laufzeiten, Zinsmodelle, Belehnung und Amortisation, Ablauf einer Finanzierung.",
        teaches: [
            { skillId: "fach.hypotheken", level: 2 },
            { skillId: "fach.tragbarkeit", level: 2 },
        ],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "tragbarkeit",
        title: "Tragbarkeit und Belehnung in der Praxis",
        description:
            "Kalkulatorischer Zinssatz, Eigenmittel, Zweitwohnungen, Tragbarkeitsrechnung bei Selbständigen.",
        teaches: [{ skillId: "fach.tragbarkeit", level: 3 }],
        durationHours: 4,
        format: "webinar",
        prerequisites: ["hypotheken-basis"],
        level: 2,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "immobilienbewertung",
        title: "Immobilienbewertung",
        description:
            "Hedonische Modelle, Realwert und Ertragswert, Plausibilisierung von Schätzungen, Sonderobjekte.",
        teaches: [{ skillId: "fach.immobilienbewertung", level: 3 }],
        durationHours: 6,
        format: "praesenz",
        prerequisites: ["hypotheken-basis"],
        level: 2,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "kreditanalyse-basis",
        title: "Kreditanalyse: Grundlagen",
        description:
            "Kreditantrag, Bonitätsbeurteilung, Sicherheiten, Kompetenzordnung und Antragsweg.",
        teaches: [
            { skillId: "fach.kreditanalyse", level: 2 },
            { skillId: "fach.kreditrisiko", level: 1 },
        ],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "bilanzanalyse",
        title: "Bilanz- und Jahresabschlussanalyse",
        description:
            "Kennzahlen, Cashflow-Analyse, Bereinigungen, Beurteilung der Ertragskraft von KMU.",
        teaches: [
            { skillId: "fach.bilanzanalyse", level: 3 },
            { skillId: "fach.kreditanalyse", level: 3 },
        ],
        durationHours: 12,
        format: "praesenz",
        prerequisites: ["kreditanalyse-basis"],
        level: 2,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "kreditrisiko-fortgeschritten",
        evidence: "entscheidungstest",
        title: "Kreditrisikobeurteilung für Fortgeschrittene",
        description:
            "Ratingmodelle, Klumpenrisiken, Frühwarnindikatoren, Portfoliosicht auf das Kreditbuch.",
        teaches: [{ skillId: "fach.kreditrisiko", level: 4 }],
        durationHours: 8,
        format: "webinar",
        prerequisites: ["bilanzanalyse"],
        level: 3,
        language: "de",
        provider: "Risk Management",
    },
    {
        id: "firmenkunden",
        title: "Firmenkundengeschäft",
        description:
            "Bedürfnisse von KMU, Betriebskredite, Leasing, Nachfolgeregelung, Zusammenspiel mit dem Zahlungsverkehr.",
        teaches: [
            { skillId: "fach.firmenkunden", level: 3 },
            { skillId: "fach.zahlungsverkehr", level: 2 },
        ],
        durationHours: 8,
        format: "praesenz",
        prerequisites: ["kreditanalyse-basis"],
        level: 2,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "recovery",
        title: "Sanierung und Recovery",
        description:
            "Frühwarnung, Sanierungskonzepte, Betreibung und Verwertung, Zusammenarbeit mit dem Rechtsdienst.",
        teaches: [{ skillId: "fach.recovery", level: 3 }],
        durationHours: 6,
        format: "praesenz",
        prerequisites: ["kreditrisiko-fortgeschritten"],
        level: 3,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "zahlungsverkehr",
        title: "Zahlungsverkehr Schweiz und international",
        description:
            "SIC, SEPA, ISO 20022, QR-Rechnung, Instant Payments, Abwicklung und Störungsfälle.",
        teaches: [{ skillId: "fach.zahlungsverkehr", level: 2 }],
        durationHours: 3,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Operations",
    },

    // ========================================================================
    // Führung
    // ========================================================================
    {
        id: "fuehrung-basis",
        title: "Führen: Grundlagen für neue Führungskräfte",
        description:
            "Rollenwechsel vom Fach zur Führung, Führungsverständnis der Acme Bank, Erwartungsklärung im Team.",
        teaches: [
            { skillId: "fue.delegation", level: 2 },
            { skillId: "fue.teamentwicklung", level: 2 },
        ],
        durationHours: 16,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "feedback",
        title: "Feedback geben und nehmen",
        description:
            "Wirksames Feedback im Alltag, schwierige Rückmeldungen, Umgang mit Abwehrreaktionen.",
        teaches: [{ skillId: "fue.feedback", level: 3 }],
        durationHours: 4,
        format: "praesenz",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "mag-fuehren",
        title: "Mitarbeitergespräche wirksam führen",
        description:
            "Vorbereitung, Zielvereinbarung, Beurteilung, Entwicklungsplanung und Dokumentation im MAG-Zyklus.",
        teaches: [
            { skillId: "fue.mag", level: 3 },
            { skillId: "fue.feedback", level: 2 },
        ],
        durationHours: 4,
        format: "webinar",
        prerequisites: ["feedback"],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "konflikt",
        title: "Konfliktmanagement im Team",
        description:
            "Konflikttypen erkennen, Eskalationsstufen, Moderation von Konfliktgesprächen, Rolle von HR.",
        teaches: [{ skillId: "fue.konflikt", level: 3 }],
        durationHours: 8,
        format: "praesenz",
        prerequisites: ["fuehrung-basis"],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "rekrutierung",
        title: "Rekrutierung und strukturierte Auswahl",
        description:
            "Anforderungsprofil, strukturiertes Interview, Beurteilungsfehler, Entscheid im Auswahlgremium.",
        teaches: [{ skillId: "fue.rekrutierung", level: 3 }],
        durationHours: 4,
        format: "webinar",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "arbeitsrecht",
        evidence: "wissenstest",
        title: "Arbeitsrecht für Führungskräfte",
        description:
            "Arbeitsvertrag, Arbeitszeit, Kündigung, Absenzen und Krankheit, Fürsorgepflicht des Arbeitgebers.",
        teaches: [
            { skillId: "fue.arbeitsrecht", level: 2 },
            { skillId: "fue.absenzen", level: 2 },
        ],
        durationHours: 4,
        format: "webinar",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR / Legal",
    },
    {
        id: "change",
        title: "Change Management für Führungskräfte",
        description:
            "Veränderungskurve, Kommunikation in der Unsicherheit, Umgang mit Widerstand, Beteiligung des Teams.",
        teaches: [{ skillId: "fue.changeManagement", level: 3 }],
        durationHours: 8,
        format: "praesenz",
        prerequisites: ["fuehrung-basis"],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },

    // ========================================================================
    // Digital / AI
    // ========================================================================
    {
        id: "ai-risiko",
        evidence: "wissenstest",
        title: "Risikobewusster Umgang mit generativer AI",
        description:
            "Pflichtkurs zur Nutzung generativer KI: Datenschutz, Bankkundengeheimnis, Halluzinationen, Weisung A-035.",
        teaches: [
            { skillId: "dig.aiRisiken", level: 2 },
            { skillId: "reg.datenschutz", level: 1 },
        ],
        durationHours: 1,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "learn.acme-bank.ch",
    },
    {
        id: "ai-grundlagen",
        title: "Grundlagentraining AI",
        description:
            "Wie generative KI funktioniert, was sie kann und was nicht, sinnvolle Einsatzfelder im Bankalltag.",
        teaches: [{ skillId: "dig.aiGrundlagen", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "learn.acme-bank.ch",
    },
    {
        id: "prompting",
        title: "Prompt Engineering für den Arbeitsalltag",
        description:
            "Wirksame Prompts, Arbeit mit Instruktionen und Dokumenten, Grenzen und Prüfpflicht der Resultate.",
        teaches: [{ skillId: "dig.promptEngineering", level: 3 }],
        durationHours: 3,
        format: "webinar",
        prerequisites: ["ai-grundlagen"],
        level: 1,
        language: "de",
        provider: "Data & Automation",
    },
    {
        id: "excel-analysen",
        title: "Excel für Analysen",
        description:
            "Pivot, Power Query, saubere Datenmodelle, häufige Fehlerquellen in Auswertungen.",
        teaches: [{ skillId: "dig.excel", level: 3 }],
        durationHours: 6,
        format: "webinar",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Data & Automation",
    },
    {
        id: "datenanalyse",
        title: "Datenanalyse für Fachbereiche",
        description:
            "Von der Frage zur Auswertung, Kennzahlen sauber definieren, Visualisierung und Interpretation.",
        teaches: [{ skillId: "dig.datenanalyse", level: 3 }],
        durationHours: 8,
        format: "webinar",
        prerequisites: ["excel-analysen"],
        level: 2,
        language: "de",
        provider: "Data & Automation",
    },
    {
        id: "cloud-grundlagen",
        title: "Cloud-Grundlagen (Azure)",
        description:
            "Servicemodelle, Landing Zones, Kosten und Governance, Sicherheitsanforderungen der Bank.",
        teaches: [{ skillId: "dig.cloudGrundlagen", level: 2 }],
        durationHours: 8,
        format: "webinar",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Information Technology",
    },
    {
        id: "security-engineering",
        title: "Secure Software Engineering",
        description:
            "OWASP Top 10, Secrets Management, Threat Modelling, sichere Abhängigkeiten und Code Reviews.",
        teaches: [
            { skillId: "dig.securityEngineering", level: 3 },
            { skillId: "reg.infosec", level: 3 },
        ],
        durationHours: 8,
        format: "webinar",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "Information Security",
    },
    {
        id: "it-architektur",
        title: "IT-Architektur der Acme Bank",
        description:
            "Systemlandschaft, Integrationsmuster, Architekturprinzipien und Governance im Architekturboard.",
        teaches: [{ skillId: "dig.itArchitektur", level: 3 }],
        durationHours: 6,
        format: "praesenz",
        prerequisites: ["cloud-grundlagen"],
        level: 2,
        language: "de",
        provider: "Information Technology",
    },
    {
        id: "digitale-kanaele",
        title: "Digitale Kundenkanäle",
        description:
            "E-Banking und App aus Kundensicht, Onboarding-Strecken, Support-Fälle und Kanalwechsel in der Beratung.",
        teaches: [{ skillId: "dig.digitaleKanaele", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Digital Banking",
    },

    {
        id: "excel-grundlagen",
        title: "Excel: Grundlagen für Auswertungen",
        description:
            "Pivot-Tabellen, Verweisfunktionen und saubere Datenaufbereitung für wiederkehrende Auswertungen im Tagesgeschäft.",
        teaches: [{ skillId: "dig.excel", level: 2 }],
        durationHours: 3,
        format: "e-learning",
        evidence: "wissenstest",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Data & Automation",
    },
    // ========================================================================
    // Methodik
    // ========================================================================
    {
        id: "projektmanagement",
        title: "Projektmanagement Grundlagen",
        description:
            "Projektauftrag, Planung, Risiken und Stakeholder, Steuerung und Reporting im Projektportfolio.",
        teaches: [{ skillId: "met.projektmanagement", level: 2 }],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "PMO",
    },
    {
        id: "agile",
        title: "Agile Arbeitsweisen",
        description:
            "Scrum und Kanban in der Praxis, Rollen, Backlog-Arbeit, Zusammenspiel mit klassischer Projektführung.",
        teaches: [{ skillId: "met.agile", level: 2 }],
        durationHours: 4,
        format: "webinar",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "PMO",
    },
    {
        id: "praesentation",
        title: "Präsentation und Moderation",
        description:
            "Aufbau überzeugender Präsentationen, Umgang mit Nervosität, Moderation von Sitzungen und Workshops.",
        teaches: [{ skillId: "met.praesentation", level: 3 }],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "verhandlung",
        title: "Verhandlungsführung",
        description:
            "Vorbereitung, Interessen statt Positionen, Umgang mit Preisdruck, Abschluss und Nachbereitung.",
        teaches: [{ skillId: "met.verhandlung", level: 3 }],
        durationHours: 8,
        format: "praesenz",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "selbstorganisation",
        title: "Selbstorganisation und Priorisierung",
        description:
            "Aufgabenmanagement, Umgang mit Unterbrechungen, Fokuszeiten, Zusammenarbeit im Hybridmodell.",
        teaches: [{ skillId: "met.selbstorganisation", level: 2 }],
        durationHours: 3,
        format: "webinar",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "HR Entwicklung",
    },

    {
        id: "selbstorganisation-basis",
        title: "Selbstorganisation und Priorisierung",
        description:
            "Aufgaben bündeln, Prioritäten begründen und Zusagen realistisch halten — mit Vorlagen für Wochenplanung und Übergaben.",
        teaches: [{ skillId: "met.selbstorganisation", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        evidence: "abschluss",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "schreiben-kundenkorrespondenz",
        title: "Schriftliche Kundenkorrespondenz",
        description:
            "Verständlich formulieren, Fachbegriffe übersetzen und heikle Botschaften schriftlich sauber überbringen.",
        teaches: [{ skillId: "met.schreiben", level: 2 }],
        durationHours: 2,
        format: "e-learning",
        evidence: "abschluss",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "HR Entwicklung",
    },
    // ========================================================================
    // On-the-job und soziales Lernen — die "70" und die "20"
    // ========================================================================
    // Ohne diese Einträge ist 70-20-10 ein Regler ohne Wirkung: der Katalog
    // bestand ausschliesslich aus Präsenz, Webinar und E-Learning, also aus der
    // "10". Genau das ist der übliche Befund in einem LMS-Katalog — formale
    // Kurse sind katalogisierbar, Erfahrung und Austausch sind es selten. Diese
    // Einträge sind bewusst als Bausteine mit Aufwand und Nachweis modelliert
    // und nicht als vage Empfehlungen: was nicht geplant und belegt werden
    // kann, findet im Alltag nicht statt.
    {
        id: "hospitation-kreditcenter",
        title: "Hospitation Kreditcenter",
        description:
            "Zwei Tage im Kreditcenter Firmenkunden: Dossiers mitlesen, Kreditanträge begleiten, an einer Kreditsitzung teilnehmen. Abschluss mit einer eigenen Fallbeurteilung.",
        teaches: [
            { skillId: "fach.kreditanalyse", level: 2 },
            { skillId: "fach.bilanzanalyse", level: 1 },
        ],
        durationHours: 14,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: ["kreditanalyse-basis"],
        level: 1,
        language: "de",
        provider: "Kreditcenter Firmenkunden",
    },
    {
        id: "mentoring-beratung",
        title: "Mentoring Beratung",
        description:
            "Sechs Monate begleitendes Mentoring durch eine erfahrene Beraterin oder einen erfahrenen Berater: monatliches Gespräch, zwei begleitete Kundengespräche, gemeinsame Nachbesprechung.",
        teaches: [
            { skillId: "fach.beratungsprozess", level: 3 },
            { skillId: "fach.gespraechsfuehrung", level: 3 },
        ],
        durationHours: 12,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "lernpartnerschaft-vorsorge",
        title: "Lernpartnerschaft Vorsorge",
        description:
            "Tandem mit einer Kollegin oder einem Kollegen aus der Vorsorgeberatung: vier gemeinsame Fallbesprechungen, gegenseitiges Schattenlaufen, ein gemeinsam erarbeitetes Kundenbeispiel.",
        teaches: [{ skillId: "fach.vorsorge", level: 3 }],
        durationHours: 8,
        format: "social",
        prerequisites: ["vorsorge-basis"],
        level: 2,
        language: "de",
        provider: "Fachstelle Vorsorge",
    },
    {
        id: "fachzirkel-esg",
        title: "Fachzirkel Nachhaltigkeit",
        description:
            "Monatlicher Austausch zu ESG in der Beratung: aktuelle Regulierung, Produktneuerungen, schwierige Kundenfragen aus der Praxis der Teilnehmenden.",
        teaches: [
            { skillId: "fach.esg", level: 2 },
            { skillId: "fach.esgBeratung", level: 2 },
        ],
        durationHours: 6,
        format: "social",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Fachstelle Nachhaltigkeit",
    },
    {
        id: "projektmitarbeit-digital",
        title: "Projektmitarbeit Digitalisierung",
        description:
            "Mitarbeit in einem laufenden Digitalisierungsvorhaben über ein Quartal: Anforderungen aus Kundensicht einbringen, Tests begleiten, Ergebnisse im Team vorstellen.",
        teaches: [
            { skillId: "dig.digitaleKanaele", level: 2 },
            { skillId: "met.projektmanagement", level: 2 },
        ],
        durationHours: 20,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Digital Banking",
    },
    {
        id: "shadowing-fuehrung",
        title: "Shadowing Führung",
        description:
            "Eine Führungskraft über zwei Wochen begleiten: Teamsitzungen, ein Mitarbeitendengespräch als Beobachtende, wöchentliche Reflexion.",
        teaches: [
            { skillId: "fue.feedback", level: 2 },
            { skillId: "fue.teamentwicklung", level: 1 },
        ],
        durationHours: 10,
        format: "on-the-job",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "kollegiale-fallberatung",
        title: "Kollegiale Fallberatung",
        description:
            "Strukturierte Fallberatung in einer festen Gruppe von sechs Personen: eigene schwierige Beratungssituationen einbringen und nach festem Ablauf bearbeiten.",
        teaches: [
            { skillId: "fach.einwandbehandlung", level: 2 },
            { skillId: "fue.konflikt", level: 1 },
        ],
        durationHours: 8,
        format: "social",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "stellvertretung-teamleitung",
        title: "Stellvertretung Teamleitung",
        description:
            "Die Teamleitung während einer Abwesenheit vertreten: Tagesgeschäft steuern, Teamsitzung leiten, Eskalationen entscheiden. Mit Vor- und Nachbesprechung.",
        teaches: [
            { skillId: "fue.delegation", level: 2 },
            { skillId: "fue.teamentwicklung", level: 2 },
        ],
        durationHours: 16,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: ["fuehrung-basis"],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "praxisauftrag-zahlungsverkehr",
        title: "Praxisauftrag Zahlungsverkehr",
        description:
            "Einen realen Prozessfall im Zahlungsverkehr eigenständig aufnehmen, analysieren und einen Verbesserungsvorschlag vor der Fachstelle vertreten.",
        teaches: [
            { skillId: "fach.zahlungsverkehr", level: 2 },
            { skillId: "met.prozessmanagement", level: 1 },
        ],
        durationHours: 9,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Operations",
    },
    {
        id: "peer-review-kreditantraege",
        title: "Peer-Review Kreditanträge",
        description:
            "Gegenseitige Durchsicht von Kreditanträgen im Vieraugenprinzip über ein Quartal, mit dokumentierter Rückmeldung zu Begründungsqualität und Auflagen.",
        teaches: [
            { skillId: "fach.kreditanalyse", level: 3 },
            { skillId: "fach.kreditrisiko", level: 2 },
        ],
        durationHours: 7,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: ["kreditanalyse-basis"],
        level: 2,
        language: "de",
        provider: "Kreditcenter Firmenkunden",
    },
    {
        id: "hospitation-anlagedesk",
        title: "Hospitation Anlage-Desk",
        description:
            "Drei Tage im Investment Center: Anlagevorschläge mitlesen, an der Anlagesitzung teilnehmen und ein eigenes Musterportfolio im Team vertreten.",
        teaches: [
            { skillId: "fach.anlageberatung", level: 2 },
            { skillId: "fach.portfolio", level: 2 },
        ],
        durationHours: 12,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Investment Center",
    },
    {
        id: "hospitation-compliance-desk",
        title: "Hospitation Compliance-Desk",
        description:
            "Zwei Tage bei der Compliance: Abklärungen zu Verdachtsmeldungen begleiten, Transaktionsmonitoring mitlesen und einen eigenen Fall dokumentieren.",
        teaches: [
            { skillId: "reg.gwg", level: 3 },
            { skillId: "reg.kyc", level: 3 },
        ],
        durationHours: 10,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: ["gwg-basis"],
        level: 2,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "praxisauftrag-finanzplanung",
        title: "Praxisauftrag Ganzheitliche Finanzplanung",
        description:
            "Eine vollständige Finanzplanung für eine echte Kundensituation erstellen, im Vieraugenprinzip prüfen lassen und der Kundin oder dem Kunden präsentieren.",
        teaches: [
            { skillId: "fach.finanzplanung", level: 2 },
            { skillId: "fach.vorsorge", level: 2 },
        ],
        durationHours: 10,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "job-rotation-operations",
        title: "Job-Rotation Operations",
        description:
            "Vier Wochen in der Abwicklung: Zahlungsverkehr, Nachbearbeitung und Fehlerbilder aus Sicht der Verarbeitung statt aus Sicht der Beratung.",
        teaches: [
            { skillId: "met.prozessmanagement", level: 2 },
            { skillId: "fach.zahlungsverkehr", level: 2 },
        ],
        durationHours: 12,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Operations",
    },
    {
        id: "hospitation-recovery",
        title: "Hospitation Sanierung und Recovery",
        description:
            "Zwei Tage in der Sanierung: notleidende Engagements mitverfolgen, Sanierungsoptionen abwägen und die Rolle der Beratung im Vorfeld reflektieren.",
        teaches: [
            { skillId: "fach.recovery", level: 2 },
            { skillId: "fach.kreditrisiko", level: 3 },
        ],
        durationHours: 10,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: ["kreditanalyse-basis"],
        level: 2,
        language: "de",
        provider: "Kreditcenter Firmenkunden",
    },
    {
        id: "portfolio-uebernahme-begleitet",
        title: "Begleitete Portfolio-Übernahme",
        description:
            "Ein bestehendes Kundenportfolio über drei Monate begleitet übernehmen: Erstgespräche, Übergabedokumentation und Standortbestimmung je Kundenbeziehung.",
        teaches: [
            { skillId: "fach.beratungsprozess", level: 3 },
            { skillId: "fach.produktkenntnis", level: 3 },
        ],
        durationHours: 12,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "praxisauftrag-immobilien",
        title: "Praxisauftrag Immobilienbewertung",
        description:
            "Drei Objekte selbstständig bewerten, die Bewertung begründen und mit der Einschätzung der Fachstelle abgleichen.",
        teaches: [
            { skillId: "fach.immobilienbewertung", level: 2 },
            { skillId: "fach.tragbarkeit", level: 2 },
        ],
        durationHours: 8,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Kreditausbildung",
    },
    {
        id: "teilprojektleitung",
        title: "Teilprojektleitung übernehmen",
        description:
            "Ein abgegrenztes Teilprojekt über ein Quartal führen: Planung, Statusberichte, Risiken und eine Abnahme durch die Projektleitung.",
        teaches: [
            { skillId: "met.projektmanagement", level: 3 },
            { skillId: "met.selbstorganisation", level: 2 },
        ],
        durationHours: 12,
        format: "on-the-job",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "PMO",
    },
    {
        id: "mentoring-fuehrungsnachwuchs",
        title: "Mentoring Führungsnachwuchs",
        description:
            "Sechs Monate Mentoring durch eine erfahrene Führungskraft: monatliches Gespräch, Begleitung bei zwei Teamsituationen und eine gemeinsame Standortbestimmung.",
        teaches: [
            { skillId: "fue.feedback", level: 2 },
            { skillId: "fue.teamentwicklung", level: 2 },
        ],
        durationHours: 10,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "intervision-fuehrung",
        title: "Intervision Führungsthemen",
        description:
            "Feste Gruppe von Führungskräften, die über ein halbes Jahr eigene Fälle einbringt und nach festem Ablauf kollegial bearbeitet.",
        teaches: [
            { skillId: "fue.konflikt", level: 3 },
            { skillId: "fue.mag", level: 3 },
        ],
        durationHours: 8,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "community-of-practice-kredit",
        title: "Community of Practice Kredit",
        description:
            "Monatlicher Austausch zu Grenzfällen und neuen Weisungen im Kreditgeschäft, mit rotierender Fallvorstellung aus dem eigenen Portfolio.",
        teaches: [
            { skillId: "fach.kreditanalyse", level: 3 },
            { skillId: "fach.bilanzanalyse", level: 3 },
        ],
        durationHours: 6,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 2,
        language: "de",
        provider: "Kreditcenter Firmenkunden",
    },
    {
        id: "lunch-and-learn-regulatorik",
        title: "Lunch and Learn Regulatorik-Update",
        description:
            "Kurzformat über Mittag: was sich in Weisungen und Aufsichtspraxis geändert hat und was das konkret für die Beratung bedeutet.",
        teaches: [
            { skillId: "reg.marktverhalten", level: 1 },
            { skillId: "reg.fidleg", level: 1 },
        ],
        durationHours: 3,
        format: "social",
        evidence: "abschluss",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Compliance",
    },
    {
        id: "shadowing-kundengespraeche",
        title: "Shadowing Kundengespräche",
        description:
            "Acht Kundengespräche einer erfahrenen Beraterin oder eines erfahrenen Beraters begleiten, mit strukturierter Nachbesprechung nach jedem Termin.",
        teaches: [
            { skillId: "fach.gespraechsfuehrung", level: 2 },
            { skillId: "fach.einwandbehandlung", level: 2 },
        ],
        durationHours: 6,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Vertriebsausbildung",
    },
    {
        id: "reverse-mentoring-digital",
        title: "Reverse Mentoring Digital",
        description:
            "Jüngere Mitarbeitende begleiten erfahrene Kolleginnen und Kollegen bei digitalen Werkzeugen — vier Sessions mit konkreten eigenen Anwendungsfällen.",
        teaches: [
            { skillId: "dig.excel", level: 2 },
            { skillId: "met.agile", level: 1 },
        ],
        durationHours: 6,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 0,
        language: "de",
        provider: "Data & Automation",
    },
    {
        id: "lernpartnerschaft-steuern",
        title: "Lernpartnerschaft Steuern",
        description:
            "Zwei Beratende bearbeiten über ein Quartal gemeinsam Steuerfälle aus dem eigenen Kundenbuch und ziehen eine gemeinsame Bilanz.",
        teaches: [{ skillId: "fach.steuern", level: 2 }],
        durationHours: 8,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "HR Entwicklung",
    },
    {
        id: "fachzirkel-datenanalyse",
        title: "Fachzirkel Datenanalyse",
        description:
            "Fünf Termine, in denen Teilnehmende eigene Auswertungen vorstellen, gegenseitig prüfen und die Methodik gemeinsam schärfen.",
        teaches: [
            { skillId: "dig.datenanalyse", level: 2 },
            { skillId: "dig.excel", level: 3 },
        ],
        durationHours: 8,
        format: "social",
        evidence: "transfernachweis",
        prerequisites: [],
        level: 1,
        language: "de",
        provider: "Data & Automation",
    },
]

export const BAUSTEIN_BY_ID: ReadonlyMap<string, Baustein> = new Map(
    BAUSTEINE.map((b) => [b.id, b]),
)
